import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from './firebase';
import { doc, getDoc } from 'firebase/firestore';
import { updateOrder } from './services/firebaseService';
import Header from './Header';
import AdminSidebar from './AdminSidebar';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintIcon from '@mui/icons-material/Print';
import SaveIcon from '@mui/icons-material/Save';
import ListAltIcon from '@mui/icons-material/ListAlt';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import BusinessIcon from '@mui/icons-material/Business';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import InfoIcon from '@mui/icons-material/Info';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PhoneIcon from '@mui/icons-material/Phone';

const fieldLabelClass = 'text-xs uppercase tracking-wider text-gray-500 font-semibold';
const fieldInputClass =
  'w-full bg-white text-primary border border-gray-200 focus:border-primary focus:ring-0 rounded p-2 text-sm transition-colors';
const fieldSelectClass = `${fieldInputClass} font-medium cursor-pointer appearance-none`;

const statusDotColors = {
  pending: 'bg-yellow-500',
  confirmed: 'bg-blue-500',
  processing: 'bg-purple-500',
  shipped: 'bg-indigo-500',
  delivered: 'bg-green-500',
  cancelled: 'bg-red-500',
  rejected: 'bg-red-500'
};

const OrderDetails = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [courier, setCourier] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [updating, setUpdating] = useState(false);

  const loadOrder = useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      const orderRef = doc(db, 'orders', orderId);
      const orderSnap = await getDoc(orderRef);
      
      if (orderSnap.exists()) {
        const orderData = {
          id: orderSnap.id,
          ...orderSnap.data()
        };
        setOrder(orderData);
        setSelectedStatus(orderData.status || 'processing');
        setRejectionReason(orderData.rejectionReason || '');
        setCourier(orderData.courier || '');
        setTrackingNumber(orderData.trackingNumber || '');
        setInvoiceNumber(orderData.invoiceNumber || '');
      } else {
        setError('Order not found');
      }
    } catch (err) {
      console.error('Error loading order:', err);
      setError('Failed to load order details');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  const handleUpdateStatus = useCallback(async () => {
    if (!selectedStatus) {
      alert('Please select a status');
      return;
    }

    if (selectedStatus === 'rejected' && !rejectionReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }

    if (selectedStatus === 'shipped' && (!courier.trim() || !trackingNumber.trim() || !invoiceNumber.trim())) {
      alert('Please provide courier, tracking number, and invoice number for shipped orders');
      return;
    }

    if (!window.confirm(`Update order status to "${selectedStatus}"?`)) return;

    setUpdating(true);

    try {
      const updateData = { status: selectedStatus };

      if (selectedStatus === 'rejected') {
        updateData.rejectionReason = rejectionReason.trim();
      }

      if (selectedStatus === 'shipped') {
        updateData.courier = courier.trim();
        updateData.trackingNumber = trackingNumber.trim();
      }

      if (invoiceNumber.trim()) {
        updateData.invoiceNumber = invoiceNumber.trim();
      }

      const result = await updateOrder(orderId, updateData);
      if (result.success) {
        await loadOrder();
        alert('Order status updated successfully!');
        setRejectionReason('');
        setCourier('');
        setTrackingNumber('');
      } else {
        alert('Failed to update order status: ' + result.error);
      }
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update order status');
    } finally {
      setUpdating(false);
    }
  }, [
    selectedStatus,
    rejectionReason,
    courier,
    trackingNumber,
    invoiceNumber,
    orderId,
    loadOrder
  ]);

  const formatDateTimeCompact = (date) => {
    if (!date) return 'N/A';

    let dateObj;
    if (date.toDate && typeof date.toDate === 'function') {
      dateObj = date.toDate();
    } else {
      dateObj = new Date(date);
    }

    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const hours = String(dateObj.getHours()).padStart(2, '0');
    const minutes = String(dateObj.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  const formatCurrency = (amount) => {
    const value = Number(amount);
    if (Number.isNaN(value)) return '—';
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR'
    }).format(value);
  };

  const handleViewInvoice = () => {
    if (!order) return;
    navigate(`/invoice/${order.id}`);
  };

  const handleCopyInvoiceNumber = async () => {
    const value = invoiceNumber || order?.invoiceNumber;
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
      alert('Invoice number copied to clipboard');
    } catch (err) {
      console.error('Failed to copy invoice number:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="pt-20 flex">
          <AdminSidebar activeItem="orders" />
          <div className="flex-1 min-w-0 flex items-center justify-center min-h-[calc(100vh-80px)]">
            <div className="text-center text-gray-500 text-xl">Loading order details...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="pt-20 flex">
          <AdminSidebar activeItem="orders" />
          <div className="flex-1 min-w-0 flex items-center justify-center min-h-[calc(100vh-80px)]">
            <div className="text-center text-red-500 text-xl">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="pt-20 flex">
          <AdminSidebar activeItem="orders" />
          <div className="flex-1 min-w-0 flex items-center justify-center min-h-[calc(100vh-80px)]">
            <div className="text-center text-gray-500 text-xl">Order not found</div>
          </div>
        </div>
      </div>
    );
  }

  const subtotal = Number(order.amount ?? 0);
  const shippingFees = Number(order.shippingFees ?? 0);
  const grandTotal = Number(order.totalAmount ?? order.total ?? 0);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <div className="flex-1  flex">
        <AdminSidebar activeItem="orders" />
        <div className="flex-1 min-w-0 overflow-y-auto bg-gray-50 p-4 md:p-6">
          <div className="max-w-[1280px] w-full mx-auto flex flex-col gap-6">
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => navigate('/admin-portal')}
                className="inline-flex items-center text-gray-500 hover:text-primary transition-colors text-sm w-fit group"
              >
                <ArrowBackIcon className="!w-[18px] !h-[18px] mr-1 group-hover:-translate-x-0.5 transition-transform" />
                Back to Incoming Orders
              </button>
              <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                <h2 className="text-2xl md:text-3xl font-bold text-primary m-0 leading-none">
                  {order.orderId || order.id?.slice(-8).toUpperCase()}
                </h2>
                <div className="flex gap-3 self-start md:self-auto">
                  {order.status === 'shipped' && (
                    <button
                      type="button"
                      onClick={handleViewInvoice}
                      className="border-2 border-gray-200 text-gray-600 font-bold text-sm py-2 px-4 rounded hover:bg-gray-100 transition-colors flex items-center gap-2"
                    >
                      <PrintIcon className="!w-5 !h-5" />
                      Print Invoice
                    </button>
                  )}
                </div>
              </div>
            </div>

            <hr className="border-t-2 border-gray-200" />

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
              <div className="lg:col-span-8 flex flex-col gap-6">
                <section className="bg-white border border-gray-200 rounded p-4 flex flex-col sm:flex-row gap-6 justify-between items-start sm:items-center">
                  <div className="flex flex-col gap-1">
                    <span className={fieldLabelClass}>Date &amp; Time</span>
                    <span className="text-sm font-mono text-primary">
                      {formatDateTimeCompact(order.dateTime || order.createdAt)}
                    </span>
                  </div>
                  <div className="hidden sm:block w-px h-10 bg-gray-200" />
                  <div className="flex flex-col gap-1">
                    <span className={fieldLabelClass}>Status</span>
                    <div className="inline-flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${statusDotColors[order.status] || 'bg-gray-400'}`} />
                      <span className="text-sm font-mono font-bold text-primary capitalize">
                        {order.status}
                      </span>
                    </div>
                  </div>
                  <div className="hidden sm:block w-px h-10 bg-gray-200" />
                  <div className="flex flex-col gap-1">
                    <span className={fieldLabelClass}>Total Amount</span>
                    <span className="text-xl font-bold text-primary tracking-tight">
                      {formatCurrency(grandTotal)}
                    </span>
                  </div>
                </section>

                <section className="bg-white border border-gray-200 rounded overflow-hidden flex flex-col">
                  <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center gap-2">
                    <ListAltIcon className="text-gray-500" />
                    <h3 className="text-base font-semibold text-primary m-0">Line Items</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse min-w-[640px]">
                      <thead>
                        <tr className="bg-gray-100 border-b border-gray-200">
                          <th className={`py-3 px-4 ${fieldLabelClass} w-1/2`}>Product Details</th>
                          <th className={`py-3 px-4 ${fieldLabelClass} text-right`}>Qty</th>
                          <th className={`py-3 px-4 ${fieldLabelClass} text-right`}>Unit Price</th>
                          <th className={`py-3 px-4 ${fieldLabelClass} text-right`}>Line Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {order.products?.map((item, idx) => (
                          <tr key={idx} className="border-b border-gray-200 hover:bg-gray-50 transition-colors">
                            <td className="py-4 px-4">
                              <div className="flex flex-col">
                                <span className="text-sm font-medium text-primary">
                                  {item.productName || item.name}
                                </span>
                                {item.sku && (
                                  <span className="text-xs font-mono text-gray-500 mt-1">SKU: {item.sku}</span>
                                )}
                              </div>
                            </td>
                            <td className="py-4 px-4 text-sm font-mono text-primary text-right align-top">
                              {item.quantity}
                            </td>
                            <td className="py-4 px-4 text-sm font-mono text-gray-500 text-right align-top">
                              {item.amount !== undefined ? formatCurrency(item.amount) : '—'}
                            </td>
                            <td className="py-4 px-4 text-sm font-mono text-primary font-medium text-right align-top">
                              {item.totalAmount !== undefined
                                ? formatCurrency(item.totalAmount)
                                : item.amount !== undefined
                                  ? formatCurrency(Number(item.amount) * Number(item.quantity))
                                  : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="bg-gray-50 p-4 flex flex-col gap-2 items-end">
                    <div className="flex justify-between w-full max-w-[300px]">
                      <span className="text-sm text-gray-500">Subtotal</span>
                      <span className="text-sm font-mono text-primary">{formatCurrency(subtotal)}</span>
                    </div>
                    {order.shippingFees !== undefined && (
                      <div className="flex justify-between w-full max-w-[300px]">
                        <span className="text-sm text-gray-500">Shipping Fees</span>
                        <span className="text-sm font-mono text-primary">
                          {shippingFees === 0 ? 'FREE' : formatCurrency(shippingFees)}
                        </span>
                      </div>
                    )}
                    <div className="w-full max-w-[300px] border-t border-gray-200 my-2" />
                    <div className="flex justify-between w-full max-w-[300px] items-center">
                      <span className="text-sm font-bold text-primary">Grand Total</span>
                      <span className="text-xl font-bold text-primary tracking-tight">
                        {formatCurrency(grandTotal)}
                      </span>
                    </div>
                  </div>
                </section>

                <section className="bg-white border border-gray-200 rounded overflow-hidden">
                  <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center gap-2">
                    <ReceiptLongIcon className="text-gray-500" />
                    <h3 className="text-base font-semibold text-primary m-0">Payment Proof</h3>
                  </div>
                  <div className="p-4 flex flex-col sm:flex-row gap-6">
                    {order.receiptUrl ? (
                      <button
                        type="button"
                        onClick={() => window.open(order.receiptUrl, '_blank')}
                        className="w-full sm:w-1/3 aspect-[3/4] bg-gray-50 border border-dashed border-gray-200 rounded overflow-hidden relative group"
                      >
                        <img
                          src={order.receiptUrl}
                          alt="Payment receipt"
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gray-900/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <span className="text-white text-sm font-semibold">View Full Size</span>
                        </div>
                      </button>
                    ) : (
                      <div className="w-full sm:w-1/3 aspect-[3/4] bg-gray-50 border border-dashed border-gray-200 rounded flex flex-col items-center justify-center text-gray-500 p-4">
                        <ReceiptLongIcon className="!w-12 !h-12 mb-2 opacity-50" />
                        <span className="text-sm text-center">
                          {order.paymentReceipt || 'No receipt uploaded'}
                        </span>
                      </div>
                    )}
                    <div className="flex-1 flex flex-col gap-4">
                      <div className="p-3 bg-blue-50 text-primary border border-blue-100 rounded text-sm flex gap-2 items-start mt-auto">
                        <InfoIcon className="!w-[18px] !h-[18px] shrink-0 mt-0.5" />
                        <p className="m-0">
                          Please verify the receipt image against our corporate bank account records before approving the order.
                        </p>
                      </div>
                    </div>
                  </div>
                </section>
              </div>

              <div className="lg:col-span-4 flex flex-col gap-6">
                <section className="bg-white border border-gray-200 rounded overflow-hidden">
                  <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center gap-2">
                    <BusinessIcon className="text-gray-500" />
                    <h3 className="text-base font-semibold text-primary m-0">Customer Profile</h3>
                  </div>
                  <div className="p-4 flex flex-col gap-4">
                    <div>
                      <span className={`${fieldLabelClass} block mb-1`}>Customer Name</span>
                      <span className="text-sm text-primary font-bold">{order.purchaserName}</span>
                    </div>
                    <div>
                      <span className={`${fieldLabelClass} block mb-1`}>Contact Details</span>
                      <div className="flex items-center gap-2 text-primary text-sm font-mono mb-1">
                        <PhoneIcon className="!w-4 !h-4 text-gray-500" />
                        {order.purchaserPhoneNumber}
                      </div>
                    </div>
                    <div>
                      <span className={`${fieldLabelClass} block mb-1`}>Shipping Address</span>
                      {typeof order.purchaserAddress === 'object' ? (
                        <p className="text-sm text-primary m-0 leading-relaxed border-l-2 border-gray-200 pl-3">
                          {order.purchaserAddress.line1}
                          {order.purchaserAddress.line2 && (
                            <>
                              <br />
                              {order.purchaserAddress.line2}
                            </>
                          )}
                          <br />
                          {order.purchaserAddress.city}
                          <br />
                          {order.purchaserAddress.postcode} {order.purchaserAddress.state}
                        </p>
                      ) : (
                        <p className="text-sm text-primary m-0 leading-relaxed border-l-2 border-gray-200 pl-3">
                          {order.purchaserAddress}
                        </p>
                      )}
                    </div>
                  </div>
                </section>

                <section className="bg-white border border-gray-200 rounded border-t-4 border-t-primary overflow-hidden">
                  <div className="px-4 py-4 border-b border-gray-200">
                    <h3 className="text-base font-semibold text-primary m-0 flex items-center gap-2">
                      <AdminPanelSettingsIcon />
                      Control Panel
                    </h3>
                  </div>
                  <div className="p-4 flex flex-col gap-5">
                    <div className="flex flex-col gap-2">
                      <label className={`${fieldLabelClass} font-bold`} htmlFor="order-status">
                        Update Order Status
                      </label>
                      <select
                        id="order-status"
                        value={selectedStatus}
                        onChange={(e) => setSelectedStatus(e.target.value)}
                        className={fieldSelectClass}
                        disabled={updating}
                      >
                        <option value="processing">Processing</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="shipped">Shipped</option>
                        <option value="rejected">Rejected</option>
                      </select>
                    </div>

                    <div
                      className={`flex flex-col gap-2 transition-opacity ${
                        selectedStatus === 'rejected' ? 'opacity-100' : 'opacity-50'
                      }`}
                    >
                      <label className={fieldLabelClass} htmlFor="reject-reason">
                        Reject Reason
                      </label>
                      <textarea
                        id="reject-reason"
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        placeholder="Leave empty unless rejected"
                        rows="3"
                        className={fieldInputClass}
                        disabled={updating || selectedStatus !== 'rejected'}
                      />
                    </div>

                    <hr className="border-t border-gray-200" />

                    <div className="flex flex-col gap-2">
                      <label className={fieldLabelClass} htmlFor="courier">
                        Courier Partner
                      </label>
                      <input
                        id="courier"
                        type="text"
                        value={courier}
                        onChange={(e) => setCourier(e.target.value)}
                        placeholder="e.g., Pos Laju, SPX, J&T"
                        className={fieldInputClass}
                        disabled={updating || selectedStatus !== 'shipped'}
                      />
                    </div>

                    <div className="flex flex-col gap-2">
                      <label className={fieldLabelClass} htmlFor="tracking">
                        Tracking Number
                      </label>
                      <input
                        id="tracking"
                        type="text"
                        value={trackingNumber}
                        onChange={(e) => setTrackingNumber(e.target.value)}
                        placeholder="Enter tracking ID"
                        className={`${fieldInputClass} font-mono`}
                        disabled={updating || selectedStatus !== 'shipped'}
                      />
                    </div>

                    <hr className="border-t border-gray-200" />

                    <div className="flex flex-col gap-2">
                      <label className={`${fieldLabelClass} font-bold`} htmlFor="invoice">
                        Invoice No.
                      </label>
                      <div className="relative">
                        <input
                          id="invoice"
                          type="text"
                          value={invoiceNumber}
                          onChange={(e) => setInvoiceNumber(e.target.value)}
                          placeholder="Enter invoice number"
                          className={`${fieldInputClass} font-mono pr-10`}
                          disabled={updating}
                        />
                        {invoiceNumber.trim() && (
                          <button
                            type="button"
                            onClick={handleCopyInvoiceNumber}
                            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-gray-500 transition-colors hover:bg-gray-100 hover:text-primary"
                            title="Copy invoice number"
                            disabled={updating}
                          >
                            <ContentCopyIcon sx={{ fontSize: 18 }} />
                          </button>
                        )}
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={handleUpdateStatus}
                      disabled={updating}
                      className="w-full bg-primary text-white font-bold text-sm py-2.5 px-4 rounded hover:bg-blue-900 transition-colors shadow-md flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      <SaveIcon className="!w-5 !h-5" />
                      {updating ? 'Updating...' : 'Update Order'}
                    </button>
                  </div>
                </section>
              </div>
            </div>

            <div className="h-8" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetails;

